/*
  tr-cart.js — panier latéral.

  Le contenu du tiroir n'est jamais reconstruit ici : après chaque
  modification on redemande la page à Shopify et on remplace le bloc.
  C'est le serveur qui calcule prix, remises et totaux, et un panier qui
  fait ses propres additions finit toujours par afficher un montant qui
  n'est pas celui du paiement.
*/

if (!customElements.get('tr-cart-drawer')) {
  customElements.define(
    'tr-cart-drawer',
    class TrCartDrawer extends HTMLElement {
      connectedCallback() {
        this.bind();

        // Délégation au document : les déclencheurs vivent dans l'en-tête
        // et dans les cartes produit, hors de cet élément, et certains
        // sont injectés après coup par l'éditeur de thème.
        document.addEventListener('click', (event) => {
          if (event.target.closest('[data-cart-open]')) {
            event.preventDefault();
            this.open();
          }
        });
        document.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') this.close();
        });
        document.addEventListener('tr:cart:updated', () => this.refresh(true));

        // Tout formulaire d'ajout au panier de la page est intercepté :
        // la fiche produit, mais aussi ceux qu'une section ajoutera plus
        // tard. Sans JavaScript le formulaire part normalement vers la
        // page panier, donc l'achat reste possible dans tous les cas.
        document.addEventListener('submit', (event) => {
          const form = event.target;
          if (!(form instanceof HTMLFormElement)) return;
          const action = form.getAttribute('action') || '';
          if (!action.includes('/cart/add')) return;
          event.preventDefault();
          this.add(form);
        });
      }

      async add(form) {
        this.classList.add('is-busy');
        const submit = form.querySelector('[type="submit"]');
        if (submit) submit.disabled = true;
        try {
          const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: new FormData(form),
          });
          if (!response.ok) throw new Error('add failed');
          await this.refresh(true);
        } catch (error) {
          // Un ajout refusé — rupture gagnée de vitesse, limite de
          // quantité — doit rester visible : on laisse le formulaire
          // partir vers la page panier, qui affichera la vraie raison.
          form.submit();
        } finally {
          this.classList.remove('is-busy');
          if (submit) submit.disabled = false;
        }
      }

      bind() {
        this.querySelectorAll('[data-cart-close]').forEach((node) => {
          node.addEventListener('click', () => this.close());
        });
        this.querySelectorAll('[data-cart-qty]').forEach((node) => {
          node.addEventListener('click', () => {
            this.change(Number(node.dataset.cartIndex), Number(node.dataset.cartQty), node);
          });
        });
      }

      open() {
        this.classList.add('is-open');
        this.setAttribute('aria-hidden', 'false');
        document.documentElement.style.overflow = 'hidden';
        // On donne le focus au panneau, pas au bouton de fermeture : un
        // focus programmatique sur un bouton dessine un anneau visible,
        // et le tiroir s'ouvrait avec un cadre noir autour de la croix.
        const panel = this.querySelector('.tr-cart__panel');
        if (panel) panel.focus({ preventScroll: true });
      }

      close() {
        if (!this.classList.contains('is-open')) return;
        this.classList.remove('is-open');
        this.setAttribute('aria-hidden', 'true');
        document.documentElement.style.removeProperty('overflow');
      }

      // Supprimer une ligne demandait à Shopify, attendait la réponse, puis
      // rechargeait la page entière pour reconstruire le tiroir. Pendant
      // toute cette attente — souvent une demi-seconde — le panneau entier
      // passait à 60 % d'opacité : l'écran devenait translucide sans rien
      // dire, puis se recomposait d'un coup. On ne lisait pas un panier qui
      // se met à jour, on lisait un panier qui déraille.
      //
      // La ligne concernée s'efface maintenant d'elle-même, en repliant sa
      // hauteur — un geste, pas un clignotement. Le compte et le total sont
      // repris de la réponse de Shopify, qui les renvoie déjà : plus rien
      // n'attend le rechargement pour dire la vérité. Le rechargement suit
      // en arrière-plan, et comme l'écran affiche déjà le bon état, il ne
      // se voit pas — il ne sert qu'à remettre les suggestions à jour.
      async change(line, quantity, element) {
        if (!Number.isFinite(line) || !Number.isFinite(quantity)) return;
        if (this.classList.contains('is-busy')) return;

        this.classList.add('is-busy');
        const ligne = element ? element.closest('[data-cart-line]') : null;
        if (ligne && quantity === 0) this.replier(ligne);

        try {
          const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ line, quantity }),
          });
          if (!response.ok) throw new Error('change failed');

          const panier = await response.json();
          this.annoncer(panier);
          await this.refresh(false);
        } catch (error) {
          // Rien n'est deviné : on recharge l'état réel du panier plutôt
          // que de laisser l'écran affirmer une quantité qui n'a pas pris.
          // La ligne repliée par anticipation revient avec le rechargement.
          await this.refresh(false);
        } finally {
          this.classList.remove('is-busy');
        }
      }

      // Le repli part de la hauteur mesurée : une transition depuis `auto`
      // ne s'anime pas, le navigateur n'a rien à interpoler.
      replier(ligne) {
        ligne.style.height = `${ligne.offsetHeight}px`;
        // Forcer un calcul de style entre les deux valeurs, sinon le
        // navigateur les regroupe et saute directement à la fin.
        void ligne.offsetHeight;
        ligne.classList.add('is-leaving');
        ligne.style.height = '0px';
      }

      // Le compte et le total viennent de la réponse de /cart/change.js,
      // qui renvoie le panier complet : les afficher tout de suite évite
      // qu'un total périmé reste à l'écran le temps du rechargement.
      annoncer(panier) {
        if (!panier || typeof panier.item_count !== 'number') return;
        document.querySelectorAll('[data-cart-count]').forEach((noeud) => {
          noeud.textContent = panier.item_count;
          noeud.classList.toggle('is-empty', panier.item_count === 0);
        });
      }

      async refresh(shouldOpen) {
        try {
          const html = await (await fetch(window.location.pathname + window.location.search)).text();
          const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('tr-cart-drawer');
          if (fresh) {
            const wasOpen = this.classList.contains('is-open');
            this.innerHTML = fresh.innerHTML;
            this.bind();
            if (wasOpen || shouldOpen) this.open();
          }
          const count = fresh && fresh.querySelector('[data-cart-count]');
          if (count) {
            document.querySelectorAll('[data-cart-count]').forEach((node) => {
              node.textContent = count.textContent.trim();
            });
          }
        } catch (error) {
          /* Un tiroir non rafraîchi ne doit pas empêcher d'aller au panier. */
        }
      }
    }
  );
}
